import React, { useState, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'

const API_URL = process.env.REACT_APP_ZOOMEY_API_URL || 'http://localhost:3000'

function UnsharePopup({ show, onClose, itemId, sectionType, UserId, anchorRect, isDarkMode, onUnshareSuccess }) {
  const [receivers, setReceivers] = useState([])
  const [loading, setLoading] = useState(false)
  const [unsharing, setUnsharing] = useState(null)
  const [error, setError] = useState(null)
  const popupRef = useRef(null)

  useEffect(() => {
    if (!show || !UserId || !itemId) return
    setError(null)
    fetchReceivers()
  }, [show, UserId, itemId])

  useEffect(() => {
    if (!show) return
    const handleClick = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) onClose()
    }
    const handleScroll = () => onClose()
    document.addEventListener('mousedown', handleClick)
    window.addEventListener('scroll', handleScroll, true)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [show, onClose])

  const fetchReceivers = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_URL}/api/sharing/unshare?task_id=${itemId}&sender_id=${UserId}&section_type=${sectionType}`)
      const data = await res.json()
      setReceivers(data.receivers || [])
    } catch (e) { console.error('UnsharePopup: fetch error', e) }
    setLoading(false)
  }

  const handleUnshare = async (receiverUserId) => {
    setUnsharing(receiverUserId)
    setError(null)
    try {
      const res = await fetch(`${API_URL}/api/sharing/unshare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          task_id: itemId,
          sender_id: UserId,
          receiver_id: receiverUserId,
          section_type: sectionType,
        }),
      })
      const result = await res.json()
      if (!res.ok) throw new Error(result.error || 'Failed to unshare')

      setReceivers(prev => prev.filter(r => r.user_id !== receiverUserId))
      if (onUnshareSuccess) onUnshareSuccess()
      if (receivers.length <= 1) setTimeout(() => onClose(), 400)
    } catch (e) {
      console.error('UnsharePopup: unshare error', e)
      setError('Failed to unshare')
    }
    setUnsharing(null)
  }

  if (!show) return null

  const bgColor = isDarkMode ? '#1e1e2e' : '#fff'
  const borderColor = isDarkMode ? '#444' : '#ddd'
  const textColor = isDarkMode ? '#e0e0e0' : '#333'
  const hoverBg = isDarkMode ? '#2a2a3d' : '#f5f5f5'

  return ReactDOM.createPortal(
    <div
      ref={popupRef}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: anchorRect ? anchorRect.bottom + 4 : 0,
        left: anchorRect ? Math.max(8, anchorRect.right - 260) : 0,
        zIndex: 99999,
        background: bgColor,
        border: `1px solid ${borderColor}`,
        borderRadius: '8px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
        width: '260px',
        padding: '8px 0',
      }}
    >
      <div style={{ padding: '4px 12px 6px', fontSize: '0.7rem', fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        Unshare
      </div>

      {loading ? (
        <div style={{ padding: '12px', textAlign: 'center', fontSize: '0.75rem', color: '#999' }}>Loading...</div>
      ) : receivers.length === 0 ? (
        <div style={{ padding: '12px', textAlign: 'center', fontSize: '0.75rem', color: '#999' }}>Not shared with anyone</div>
      ) : (
        receivers.map(r => {
          const isUnsharing = unsharing === r.user_id
          return (
            <div
              key={r.user_id}
              style={{
                padding: '6px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '0.78rem',
                color: textColor,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
            >
              <i className="bi bi-person-circle" style={{ fontSize: '1rem', color: '#14b8a6' }}></i>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.email}
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); handleUnshare(r.user_id) }}
                disabled={isUnsharing}
                style={{
                  background: isUnsharing ? '#ddd' : '#ef4444',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '2px 8px',
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  cursor: isUnsharing ? 'wait' : 'pointer',
                  flexShrink: 0,
                }}
              >
                {isUnsharing ? '...' : 'Unshare'}
              </button>
            </div>
          )
        })
      )}

      {error && <div style={{ padding: '4px 12px', fontSize: '0.7rem', color: '#ef4444' }}>{error}</div>}
    </div>,
    document.body
  )
}

export default UnsharePopup
